import { useCallback } from 'react';

import { GetContextMenuItems, MenuItemDef } from 'ag-grid-community';

import { exportGridToExcel } from '@/hooks/useExcelExport';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { auditHistoryMenuItem } from '@/utils/context-menu';

import { useContextMenuActions } from './useContextMenuActions';

interface IUseContextMenuItemsProps {
  openClearFieldDialog: () => void;
  openReportDialog: () => void;
  openMissingFieldsDialog: () => void;
  onOpenWorkflowHistory?: () => void;
}

export const useContextMenuItems = ({
  openClearFieldDialog,
  openReportDialog,
  openMissingFieldsDialog,
  onOpenWorkflowHistory,
}: IUseContextMenuItemsProps) => {
  const {
    appSelectedDonorSchedules,
    workflows,
    handleDeactivateWithConfirmation,
    handleReactivateWithConfirmation,
    isReactivateSchedulesEnabled,
    handlePrintTogDonorScheduleReport,
    handleWorkflowExecute,
  } = useContextMenuActions();

  const isExportToExcelEnabled = useFeatureFlag('DS_Export_To_Excel');
  const exportAutoFilter = useFeatureFlag('Export_To_Excel_Filter_Dropdown');
  const isWorkflowStatusPageEnabled = useFeatureFlag('Workflow_Status_Page');

  const getContextMenuItems = useCallback<GetContextMenuItems>(
    ({ node, api }) => {
      const selectedCount = appSelectedDonorSchedules.length;
      const hasSelection = selectedCount > 0;

      const items: (string | MenuItemDef)[] = [];

      const donorScheduleId: string | undefined = node?.data?.id;
      if (donorScheduleId) {
        items.push(auditHistoryMenuItem({ rowId: donorScheduleId }));
      }

      if (hasSelection) {
        if (items.length > 0) {
          items.push('separator');
        }

        items.push({
          name: 'Deactivate',
          icon: '<i class="pi pi-trash"></i>',
          action: handleDeactivateWithConfirmation,
        });

        if (isReactivateSchedulesEnabled) {
          items.push({
            name: 'Reactivate',
            icon: '<i class="pi pi-replay"></i>',
            action: handleReactivateWithConfirmation,
          });
        }

        items.push({
          name: 'Clear Field',
          icon: '<i class="pi pi-eraser"></i>',
          action: openClearFieldDialog,
        });

        items.push({
          name: 'Missing Fields',
          icon: '<i class="pi pi-question"></i>',
          action: openMissingFieldsDialog,
        });

        items.push({
          name: 'Print',
          icon: '<i class="pi pi-file"></i>',
          subMenu: [
            {
              name: 'SO By Date',
              icon: '<i class="pi pi-calendar"></i>',
              action: openReportDialog,
            },
            {
              name: 'SO By Donor',
              icon: '<i class="pi pi-user"></i>',
              action: handlePrintTogDonorScheduleReport,
            },
          ],
        });

        items.push({
          name: 'Workflows',
          icon: '<i class="pi pi-wrench"></i>',
          subMenu: [
            ...workflows.map((workflow) => ({
              name: workflow.label,
              icon: '<i class="pi pi-play-circle"></i>',
              action: () => handleWorkflowExecute(workflow),
            })),
            ...(isWorkflowStatusPageEnabled
              ? [
                  {
                    name: 'Workflow History',
                    icon: '<i class="pi pi-history"></i>',
                    disabled: selectedCount !== 1,
                    action: () => onOpenWorkflowHistory?.(),
                  },
                ]
              : []),
          ],
        });
      }

      if (isExportToExcelEnabled) {
        if (items.length > 0) {
          items.push('separator');
        }
        items.push({
          name: 'Export to Excel',
          icon: '<span class="ag-icon ag-icon-excel"/>',
          action: () =>
            exportGridToExcel(api, {
              sheetName: 'Donor Schedules',
              filename: 'donor-schedules.xlsx',
              autoFilter: exportAutoFilter,
            }),
        });
      }

      return items;
    },
    [
      appSelectedDonorSchedules,
      workflows,
      isExportToExcelEnabled,
      exportAutoFilter,
      isWorkflowStatusPageEnabled,
      handleDeactivateWithConfirmation,
      handleReactivateWithConfirmation,
      isReactivateSchedulesEnabled,
      handlePrintTogDonorScheduleReport,
      handleWorkflowExecute,
      openClearFieldDialog,
      openReportDialog,
      openMissingFieldsDialog,
      onOpenWorkflowHistory,
    ],
  );

  return { getContextMenuItems };
};
